import React, { useEffect, useState } from 'react';
import { IndicatorTypeSelector, ComparisonOperatorSelector } from '../Selectors';
import { ParameterEditor } from '../Form';
import { useIndicatorSchemas } from '../../../hooks/useTrendlineStrategy';
import { getDefaultIndicatorParameters } from '../../../utils/strategyUtils';

/**
 * CompositeConditionBuilder - Form for building composite conditions (Indicator vs Indicator)
 * 
 * @param {Object} props
 * @param {Object} props.condition - Current condition data
 * @param {Function} props.onChange - Change handler (updatedCondition) => void
 * @param {Object} props.errors - Validation errors
 * @param {boolean} props.disabled - Whether form is disabled
 * @param {Object} props.rulesSchema - Rules schema from API
 * @param {Object} props.indicatorSchemas - Indicator schemas from API
 */
const CompositeConditionBuilder = ({
    condition,
    onChange,
    errors = {},
    disabled = false,
    rulesSchema = null,
    indicatorSchemas = null,
}) => {
    const hookData = useIndicatorSchemas();
    const schemas = indicatorSchemas || hookData.schemas;
    const loading = indicatorSchemas ? false : hookData.loading;

    const [localCondition, setLocalCondition] = useState(condition || {
        conditionType: 'COMPOSITE',
        operator: 'CROSSED_UP',
        leftIndicator: { type: '', parameters: {} },
        rightIndicator: { type: '', parameters: {} },
    });

    useEffect(() => {
        if (condition) {
            setLocalCondition(condition);
        }
    }, [condition]);

    const handleIndicatorChange = (side, indicatorType) => {
        const defaultParams = getDefaultIndicatorParameters(indicatorType);
        const updated = {
            ...localCondition,
            [side]: {
                type: indicatorType,
                indicatorType: indicatorType,
                parameters: defaultParams,
            },
        };
        setLocalCondition(updated);
        onChange(updated);
    };

    const handleOperatorChange = (operator) => {
        const updated = { ...localCondition, operator };
        setLocalCondition(updated);
        onChange(updated);
    };

    const handleParameterChange = (side, paramName, value) => {
        const updated = {
            ...localCondition,
            [side]: {
                ...localCondition[side],
                parameters: {
                    ...localCondition[side].parameters,
                    [paramName]: value,
                },
            },
        };
        setLocalCondition(updated);
        onChange(updated);
    };

    const getParameterSchema = (indicatorType) => {
        if (!schemas || !indicatorType) return [];
        const schema = schemas[indicatorType];
        if (!schema?.parameters) return [];

        return schema.parameters.map(param => ({
            name: param.name,
            label: param.description || param.name,
            type: param.type,
            required: param.required,
            defaultValue: param.defaultValue,
            minValue: param.minValue,
            maxValue: param.maxValue,
            description: param.description,
            unit: param.unit,
            allowedValues: param.allowedValues,
        }));
    };

    if (loading) {
        return (
            <div className="flex justify-center py-6">
                <span className="loading loading-spinner loading-md"></span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4">
                <h4 className="text-sm font-medium text-base-content/70">Comparison Operator</h4>
                <div className="w-full sm:w-1/2">
                    <ComparisonOperatorSelector
                        value={localCondition.operator}
                        onChange={handleOperatorChange}
                        error={errors.operator}
                        disabled={disabled}
                        operators={rulesSchema?.conditionTypes?.COMPOSITE?.operators}
                        rulesSchema={rulesSchema}
                        required
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Indicator */}
                <div className="bg-base-200/50 p-4 rounded-lg border border-base-300">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                        <span className="badge badge-primary badge-xs"></span> First Indicator (Left)
                    </h4>

                    <div className="space-y-4">
                        <IndicatorTypeSelector
                            value={localCondition.leftIndicator?.type || localCondition.leftIndicator?.indicatorType || ''}
                            onChange={(val) => handleIndicatorChange('leftIndicator', val)}
                            error={errors.leftIndicator?.type}
                            disabled={disabled}
                            schemas={schemas}
                            label="Indicator Type"
                            required
                        />

                        {(localCondition.leftIndicator?.type || localCondition.leftIndicator?.indicatorType) && (
                            <ParameterEditor
                                title="Parameters"
                                parameters={getParameterSchema(localCondition.leftIndicator.type || localCondition.leftIndicator.indicatorType)}
                                values={localCondition.leftIndicator.parameters || {}}
                                onChange={(name, val) => handleParameterChange('leftIndicator', name, val)}
                                errors={errors.leftIndicator?.parameters || {}}
                                disabled={disabled}
                                columns={1}
                            />
                        )}
                    </div>
                </div>

                {/* Right Indicator */}
                <div className="bg-base-200/50 p-4 rounded-lg border border-base-300">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-secondary mb-4 flex items-center gap-2">
                        <span className="badge badge-secondary badge-xs"></span> Second Indicator (Right)
                    </h4>

                    <div className="space-y-4">
                        <IndicatorTypeSelector
                            value={localCondition.rightIndicator?.type || localCondition.rightIndicator?.indicatorType || ''}
                            onChange={(val) => handleIndicatorChange('rightIndicator', val)}
                            error={errors.rightIndicator?.type}
                            disabled={disabled}
                            schemas={schemas}
                            label="Indicator Type"
                            required
                        />

                        {(localCondition.rightIndicator?.type || localCondition.rightIndicator?.indicatorType) && (
                            <ParameterEditor
                                title="Parameters"
                                parameters={getParameterSchema(localCondition.rightIndicator.type || localCondition.rightIndicator.indicatorType)}
                                values={localCondition.rightIndicator.parameters || {}}
                                onChange={(name, val) => handleParameterChange('rightIndicator', name, val)}
                                errors={errors.rightIndicator?.parameters || {}}
                                disabled={disabled}
                                columns={1}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Helper text */}
            <div className="text-xs text-base-content/40 italic text-center">
                Example: EMA(9) {localCondition.operator ? localCondition.operator.replace(/_/g, ' ') : 'crosses'} SMA(21)
            </div>
        </div>
    );
};

export default CompositeConditionBuilder;
